import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { NeuButton } from "../../components/neu/Neu";
import { deleteCompany, requestArchiveCode } from "../../api/platform";
import { ArchiveWarning } from "./ArchiveSteps";
import { ArchiveCodeStep } from "./ArchiveCodeStep";

export const ArchiveCompanyDialog = ({ company, open, onOpenChange, reload }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const reset = () => { setStep(1); setCode(""); setBusy(false); setSentTo(""); };
  const close = (next) => { onOpenChange(next); if (!next) reset(); };
  const label = `archive-company-${company?.id}`;
  const digits = code.replace(/\D/g, "").slice(0, 6);

  const requestCode = async () => {
    setBusy(true);
    try {
      const res = await requestArchiveCode(company.id);
      setSentTo(res.email); setStep(2);
      toast.success(t("archiveCompany.codeSent", { email: res.email }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("archiveCompany.error"));
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await deleteCompany(company.id, digits);
      toast.success(t("archiveCompany.done", { name: company.name }));
      close(false); reload?.();
    } catch (e) {
      const d = e?.response?.data?.detail || "error";
      toast.error(t(`archiveCompany.err.${d}`, { defaultValue: t("archiveCompany.error") }));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid={label} className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            {step === 1 ? <AlertTriangle className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
            {t(`archiveCompany.title${step}`)}
          </DialogTitle>
        </DialogHeader>
        {step === 1
          ? <ArchiveWarning company={company} />
          : <ArchiveCodeStep label={label} email={sentTo} code={code} setCode={setCode}
                              onResend={requestCode} busy={busy} />}
        <div className="mt-2 flex items-center justify-end gap-2">
          <NeuButton data-testid={`${label}-cancel`} onClick={() => close(false)} disabled={busy}>
            {t("common.cancel")}
          </NeuButton>
          {step === 1 ? (
            <NeuButton data-testid={`${label}-next`} variant="danger"
                       onClick={requestCode} disabled={busy}
                       className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {busy ? t("archiveCompany.sending") : t("archiveCompany.sendCode")}
            </NeuButton>
          ) : (
            <NeuButton data-testid={`${label}-confirm`} variant="danger"
                       disabled={digits.length !== 6 || busy} onClick={confirm}
                       className="flex items-center gap-2">
              <Trash2 className="h-4 w-4" />
              {busy ? t("archiveCompany.working") : t("archiveCompany.confirm")}
            </NeuButton>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
